import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Print as PrintIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
} from '@mui/icons-material';

// Props interface for the component
interface HailRatesViewProps {
    formData: any;
    onBack: () => void;
    onSave: () => void;
}

/**
 * HailRatesView Component
 *
 * Displays the rates view after form submission with a summary section and rates table.
 * This component mimics the design from the second image in the requirements.
 */
export const HailRatesView: React.FC<HailRatesViewProps> = ({
    formData,
    onBack,
    onSave,
}) => {
    // Format date for display
    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        });
    };

    // Mock data for the rates table
    const ratesData = [
        {
            id: 1,
            favorite: true,
            carrier: 'RCIS',
            basic: '8.00%',
            dda: '7.50%',
            comp2: '12.00%',
            xs20ip: '3.50%',
            cashDiscount: '0% Due 7/1',
            appointed: 'Yes',
        },
        {
            id: 2,
            favorite: false,
            carrier: 'NAU',
            basic: '8.10%',
            dda: '7.65%',
            comp2: '12.15%',
            xs20ip: '2.85%',
            cashDiscount: '7% Due w/App',
            appointed: 'Pending',
        },
        {
            id: 3,
            favorite: false,
            carrier: 'Rain & Hail',
            basic: '8.50%',
            dda: '8.0%',
            comp2: '12.50%',
            xs20ip: '3.35%',
            cashDiscount: '0%',
            appointed: 'Yes',
        },
        {
            id: 4,
            favorite: false,
            carrier: 'AgriSompo',
            basic: '9.25%',
            dda: '8.75%',
            comp2: '11.75%',
            xs20ip: '4.00%',
            cashDiscount: '8% Due w/App',
            appointed: 'No',
        },
    ];

    // Extract values from formData for the summary section
    const {
        account = 'Wick Brothers Farms LLC',
        contact = 'John Wick',
        state = 'MT',
        county = 'Cascade',
        legal = '020N005E',
        crop = 'Wheat',
        selectedCarriers = ['RCIS', 'NAU', 'Rain & Hail', 'AgriSompo'],
        selectedHailProducts = ['Basic', 'DDA', 'Comp2', 'XS20IP'],
        targetDate = '5/1/2025',
    } = formData || {};

    return (
        <Box>
            {/* Header with tabs */}
            <Box
                sx={{
                    bgcolor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">Hail Pricing Tool</Typography>
                </Box>
                <Box sx={{ ml: 2, display: 'flex' }}>
                    <Button
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 0,
                            borderBottom: '2px solid #649e9b',
                            color: '#649e9b',
                        }}
                        startIcon={<EditIcon fontSize="small" />}
                    >
                        Rates
                    </Button>
                    <Button
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 0,
                            color: 'text.secondary',
                        }}
                    >
                        Loss Compare
                    </Button>
                </Box>
            </Box>

            {/* Summary section */}
            <Box sx={{ p: 3 }}>
                {/* Summary header with Last Edited info */}
                <Box
                    sx={{
                        p: 2,
                        bgcolor: '#f9f9f9',
                        borderRadius: '4px',
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography>
                        State: MT County: {county} Legal: {legal} Crop: {crop}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Last Edited on 2.20.2025 17:30
                    </Typography>
                </Box>

                {/* Client information */}
                <Box sx={{ py: 2 }}>
                    <Grid container spacing={2}>
                        <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Typography sx={{ mr: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <i
                                        className="material-icons"
                                        style={{
                                            fontSize: '1rem',
                                            verticalAlign: 'middle',
                                            marginRight: '4px',
                                        }}
                                    >
                                        business
                                    </i>
                                </Box>
                                {account}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Typography sx={{ mr: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <i
                                        className="material-icons"
                                        style={{
                                            fontSize: '1rem',
                                            verticalAlign: 'middle',
                                            marginRight: '4px',
                                        }}
                                    >
                                        phone
                                    </i>
                                </Box>
                                (406) 555-5555
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Typography sx={{ mr: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <i
                                        className="material-icons"
                                        style={{
                                            fontSize: '1rem',
                                            verticalAlign: 'middle',
                                            marginRight: '4px',
                                        }}
                                    >
                                        email
                                    </i>
                                </Box>
                                john@wickbrothers.com
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Typography sx={{ mr: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <i
                                        className="material-icons"
                                        style={{
                                            fontSize: '1rem',
                                            verticalAlign: 'middle',
                                            marginRight: '4px',
                                        }}
                                    >
                                        person
                                    </i>
                                </Box>
                                {contact}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Summary details grid */}
                <Grid container spacing={2} sx={{ py: 2 }}>
                    <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="text.secondary">
                            State:
                        </Typography>
                        <Typography>{state}</Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="text.secondary">
                            County:
                        </Typography>
                        <Typography>{county}</Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="text.secondary">
                            Legal:
                        </Typography>
                        <Typography>{legal}</Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="text.secondary">
                            Crop:
                        </Typography>
                        <Typography>{crop}</Typography>
                    </Grid>
                </Grid>

                <Box sx={{ py: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Carriers:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mt: 1,
                                }}
                            >
                                {selectedCarriers.map((carrier: string) => (
                                    <Chip
                                        key={carrier}
                                        label={carrier}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ borderRadius: '16px' }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Products:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mt: 1,
                                }}
                            >
                                {selectedHailProducts.map((product: string) => (
                                    <Chip
                                        key={product}
                                        label={product}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ borderRadius: '16px' }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary">
                        Target Date:{' '}
                        <Typography
                            component="span"
                            variant="body2"
                            fontWeight="medium"
                        >
                            {targetDate}
                        </Typography>
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Rates Table Section */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Rates
                    </Typography>

                    <TableContainer
                        component={Paper}
                        sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}
                    >
                        <Table>
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell align="center">
                                        Favorite
                                    </TableCell>
                                    <TableCell>Carrier</TableCell>
                                    <TableCell align="center">Basic</TableCell>
                                    <TableCell align="center">DDA</TableCell>
                                    <TableCell align="center">Comp2</TableCell>
                                    <TableCell align="center">XS20IP</TableCell>
                                    <TableCell align="center">
                                        Cash w/App Discount
                                    </TableCell>
                                    <TableCell align="center">
                                        Appointed
                                    </TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ratesData.map(row => (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                bgcolor: '#fafafa',
                                            },
                                            '&:hover': { bgcolor: '#f0f7f5' },
                                        }}
                                    >
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    bgcolor: row.favorite
                                                        ? '#649e9b'
                                                        : '#e0e0e0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mx: 'auto',
                                                }}
                                            >
                                                {row.favorite && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: 'white',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        ✓
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.carrier}
                                                size="small"
                                                sx={{
                                                    borderRadius: '16px',
                                                    bgcolor:
                                                        row.carrier === 'RCIS'
                                                            ? '#e6f3f0'
                                                            : row.carrier ===
                                                                'NAU'
                                                              ? '#e6f6e6'
                                                              : row.carrier ===
                                                                  'Rain & Hail'
                                                                ? '#e6e6f6'
                                                                : '#f6f0e6',
                                                    color:
                                                        row.carrier === 'RCIS'
                                                            ? '#4a9e8c'
                                                            : row.carrier ===
                                                                'NAU'
                                                              ? '#4a9e4a'
                                                              : row.carrier ===
                                                                  'Rain & Hail'
                                                                ? '#5b4a9e'
                                                                : '#9e8c4a',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.basic}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.dda}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.comp2}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.xs20ip}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.cashDiscount}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={row.appointed}
                                                size="small"
                                                sx={{
                                                    borderRadius: '16px',
                                                    bgcolor:
                                                        row.appointed === 'Yes'
                                                            ? '#e6f3e6'
                                                            : row.appointed ===
                                                                'Pending'
                                                              ? '#f6f0e6'
                                                              : '#f6e6e6',
                                                    color:
                                                        row.appointed === 'Yes'
                                                            ? '#4a9e4a'
                                                            : row.appointed ===
                                                                'Pending'
                                                              ? '#9e8c4a'
                                                              : '#9e4a4a',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small">
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ArrowBackIcon />}
                            onClick={onBack}
                        >
                            Back
                        </Button>

                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={onSave}
                        >
                            Save Quote
                        </Button>

                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Rows per page: 5 | 1-4 of 4
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
